import React from "react";
import { Col, Row, Typography, Collapse, Divider, Alert } from "antd";
import { CheckCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const VendorProfile = () => {
  return (
    <Row type="flex">
      <Col span={20} offset={1}>
        <Title level={2}>i4 Vendor Profile Guide</Title>
        <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
          Your i4 Vendor Profile is the foundation of your Pre-Qualification Questionnaire (PQQ). 
          This comprehensive guide will help you complete all required sections accurately and efficiently.
        </Paragraph>

        <Alert
          message="Important"
          description="All sections of your vendor profile must be completed before you can submit a PQQ. 
          You can track your completion progress on the Dashboard."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Divider />

        <Title level={4}>Frequently Asked Questions</Title>

        <Collapse defaultActiveKey={['1']} ghost>
          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What is the i4 Vendor Profile and why do I need to complete it?
              </span>
            } 
            key="1"
          >
            <Paragraph>
              The i4 Vendor Profile is a comprehensive collection of information about your company 
              that forms the basis of your Pre-Qualification Questionnaire. It includes:
            </Paragraph>
            <ul>
              <li>Basic company information and organizational structure</li>
              <li>Financial data and reports</li>
              <li>Safety records and compliance information</li>
              <li>Insurance and bonding details</li>
              <li>Project history and experience</li>
              <li>Key personnel information</li>
            </ul>
            <Paragraph>
              This profile must be completed before you can submit a PQQ for any project. The information 
              you provide is used to evaluate your company's qualifications and capabilities.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I navigate through the different sections of the profile?
              </span>
            } 
            key="2"
          >
            <Paragraph>
              The vendor profile is organized into multiple sections accessible through a step-by-step 
              process. You can navigate between sections using the progress indicator at the top of the 
              page. Each section must be completed before moving to the next, though you can save your 
              progress and return later.
            </Paragraph>
            <Paragraph>
              <Text strong>Key Sections Include:</Text>
            </Paragraph>
            <ol>
              <li><Text strong>Basic Details:</Text> Company name, address, contact information, and legal structure</li>
              <li><Text strong>Financial Information:</Text> Revenue, financial statements, ratios, and banking details</li>
              <li><Text strong>Safety Records:</Text> EMR rates, OSHA forms, safety policies, and programs</li>
              <li><Text strong>Insurance & Bonding:</Text> Coverage details and bonding capacity</li>
              <li><Text strong>Experience & Projects:</Text> Past projects, contracts in progress, and performance history</li>
              <li><Text strong>Key Personnel:</Text> Information about owners, executives, and key staff</li>
            </ol>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What documents do I need to upload?
              </span>
            } 
            key="3"
          >
            <Paragraph>
              You'll need to upload several documents throughout the profile completion process. For a 
              complete list, see the <Text strong>Required Documentation</Text> section. Key documents include:
            </Paragraph>
            <ul>
              <li>Audited Financial Statements (most recent)</li>
              <li>Entity Organization Chart</li>
              <li>Insurance Certificates</li>
              <li>EMR Broker Letter (most recent 3 years)</li>
              <li>OSHA 300a Forms (most recent 3 years)</li>
              <li>Safety Policies and Programs</li>
              <li>Code of Conduct/Compliance Manual</li>
            </ul>
            <Paragraph>
              <Text strong>File Format Requirements:</Text> Most documents should be in PDF format. 
              Ensure files are clear, legible, and not password-protected.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Can I enter decimal numbers or negative values in numeric fields?
              </span>
            } 
            key="4"
          >
            <Paragraph>
              Yes! Numeric fields throughout the profile accept decimal values (e.g., 1,234.56) and 
              negative numbers where applicable. This is particularly useful for:
            </Paragraph>
            <ul>
              <li>Financial figures (revenue, amounts, values)</li>
              <li>Percentages and ratios</li>
              <li>EMR rates and safety metrics</li>
            </ul>
            <Paragraph>
              Simply type the number with a decimal point or minus sign as needed. The system will 
              validate and format the input appropriately.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I know if my profile is complete?
              </span>
            } 
            key="5"
          >
            <Paragraph>
              Your profile completion status is displayed on the Dashboard as a percentage. You can 
              also see which sections are complete, incomplete, or in progress. A profile is considered 
              complete when:
            </Paragraph>
            <ul>
              <li>All required fields are filled out</li>
              <li>All required documents are uploaded</li>
              <li>The completion percentage shows 100%</li>
            </ul>
            <Paragraph>
              Once your profile is 100% complete, you'll be able to proceed with payment and PQQ submission.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Can I save my progress and come back later?
              </span>
            } 
            key="6"
          >
            <Paragraph>
              Yes! Your progress is automatically saved as you complete each section. You can exit 
              the profile at any time and return later to continue where you left off. Your data will 
              be preserved, and you can edit any section before final submission.
            </Paragraph>
            <Paragraph>
              <Text strong>Note:</Text> Make sure to click "Save" or "Next" buttons to ensure your 
              information is properly saved before navigating away from a section.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What if I make a mistake or need to update information?
              </span>
            } 
            key="7"
          >
            <Paragraph>
              You can edit any section of your profile at any time before submitting a PQQ. Simply 
              navigate back to the section you want to update, make your changes, and save. If you've 
              already submitted a PQQ and need to update information, you must notify the system within 
              7 days of any changes, as outlined in the Overview section.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                I'm having trouble uploading a document. What should I do?
              </span>
            } 
            key="8"
          >
            <Paragraph>
              If you encounter issues uploading documents, try the following:
            </Paragraph>
            <ol>
              <li>Ensure the file is in PDF format (unless otherwise specified)</li>
              <li>Check that the file size is within the allowed limit</li>
              <li>Verify the file is not password-protected or corrupted</li>
              <li>Try using a different browser or clearing your browser cache</li>
              <li>Ensure you have a stable internet connection</li>
            </ol>
            <Paragraph>
              If problems persist, contact support at <Text strong>info@i4strategies.com</Text> or 
              call <Text strong>212-909-9580</Text>.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider />

        <Title level={4}>Step-by-Step Process</Title>
        <Paragraph>
          Follow these steps to complete your vendor profile:
        </Paragraph>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            <Text strong>Start from the Dashboard:</Text> Click on "i4Profile" in the navigation menu 
            or use the link on your Dashboard.
          </li>
          <li>
            <Text strong>Complete Basic Details:</Text> Enter your company's basic information, including 
            legal name, address, and contact details.
          </li>
          <li>
            <Text strong>Fill Financial Sections:</Text> Enter financial data, upload financial statements, 
            and provide banking information.
          </li>
          <li>
            <Text strong>Add Safety Information:</Text> Enter EMR rates, upload OSHA forms, and provide 
            safety program details.
          </li>
          <li>
            <Text strong>Upload Required Documents:</Text> Upload all required documentation as specified 
            in each section.
          </li>
          <li>
            <Text strong>Review and Verify:</Text> Review all sections to ensure accuracy and completeness.
          </li>
          <li>
            <Text strong>Check Completion Status:</Text> Return to the Dashboard to verify your profile 
            shows 100% completion.
          </li>
        </ol>

        <Divider />

        <Title level={4}>Tips for Success</Title>
        <ul style={{ paddingLeft: '20px' }}>
          <li>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Gather all required documents before starting to save time
          </li>
          <li>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Double-check all numeric entries for accuracy
          </li>
          <li>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Ensure document uploads are clear and legible
          </li>
          <li>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Save your progress frequently
          </li>
          <li>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
            Review the Required Documentation section before beginning
          </li>
        </ul>

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Still need help?</Text> Visit the <Text strong>Support</Text> section for contact 
            information, or review the <Text strong>Required Documentation</Text> section for a complete 
            list of needed materials.
          </Paragraph>
        </div>
      </Col>
    </Row>
  );
};

export default VendorProfile;
